import React, { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { newsletterAPI, articlesAPI, categoriesAPI } from '../../lib/api';
import { Loader2, Mail, Users, TrendingUp, Trash2, Download, Send, FileText, Zap, Folder, Bell, PenLine, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatDateShort } from '../../lib/utils';

const TEMPLATE_TYPES = [
  { id: 'weekly_digest', name: 'Weekly Digest', icon: FileText, description: 'Top stories from the past week', color: 'bg-blue-500' },
  { id: 'breaking_news', name: 'Breaking News', icon: Zap, description: 'Urgent single-story alert', color: 'bg-red-500', needsArticle: true },
  { id: 'category_spotlight', name: 'Category Spotlight', icon: Folder, description: 'Latest from a category', color: 'bg-purple-500', needsCategory: true },
  { id: 'new_article', name: 'New Article', icon: Bell, description: 'Announce a new article', color: 'bg-green-500', needsArticle: true },
  { id: 'custom', name: 'Custom', icon: PenLine, description: 'Write your own content', color: 'bg-gray-500' },
];

export const AdminNewsletter = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  
  // Newsletter dialog state
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sending, setSending] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  
  // For article/category selection
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedArticleId, setSelectedArticleId] = useState('');
  const [selectedCategorySlug, setSelectedCategorySlug] = useState('');
  
  // Custom template state
  const [customSubject, setCustomSubject] = useState('');
  const [customContent, setCustomContent] = useState('');
  
  const limit = 20;

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, subscribersRes, articlesRes, categoriesRes] = await Promise.all([
        newsletterAPI.getStats(),
        newsletterAPI.getSubscribers({ skip: page * limit, limit }),
        articlesAPI.getAll({ status: 'published', limit: 50 }),
        categoriesAPI.getAll()
      ]);
      setStats(statsRes.data);
      setSubscribers(subscribersRes.data.subscribers);
      setTotal(subscribersRes.data.total);
      setArticles(articlesRes.data.articles || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Error fetching newsletter data:', error);
      toast.error('Failed to load newsletter data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;
    
    try {
      await newsletterAPI.deleteSubscriber(id);
      toast.success('Subscriber deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete subscriber');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await newsletterAPI.export();
      const { csv, count } = res.data;
      
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${count} subscribers`);
    } catch (error) {
      toast.error('Failed to export subscribers');
    } finally {
      setExporting(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    setSelectedTemplate(template);
    setPreviewHtml('');
    setPreviewSubject('');
    setSelectedArticleId('');
    setSelectedCategorySlug('');
    
    // Auto-load preview for templates that don't need selection
    if (template.id === 'weekly_digest') {
      await loadTemplatePreview(template.id);
    } else if (template.id === 'custom') {
      setCustomSubject('');
      setCustomContent('');
    }
  };

  const loadTemplatePreview = async (templateId, articleId = null, categorySlug = null) => {
    setLoadingPreview(true);
    try {
      let res;
      switch (templateId) {
        case 'weekly_digest':
          res = await newsletterAPI.getWeeklyDigest();
          break;
        case 'breaking_news':
          res = await newsletterAPI.getBreakingNews(articleId);
          break;
        case 'category_spotlight':
          res = await newsletterAPI.getCategorySpotlight(categorySlug);
          break;
        case 'new_article':
          res = await newsletterAPI.getNewArticle(articleId);
          break;
        default:
          return;
      }
      setPreviewHtml(res.data.html);
      setPreviewSubject(res.data.subject);
    } catch (error) {
      console.error('Failed to load preview:', error);
      toast.error('Failed to load template preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleArticleSelect = async (articleId) => {
    setSelectedArticleId(articleId);
    if (articleId && selectedTemplate) {
      await loadTemplatePreview(selectedTemplate.id, articleId);
    }
  };

  const handleCategorySelect = async (categorySlug) => {
    setSelectedCategorySlug(categorySlug);
    if (categorySlug && selectedTemplate) {
      await loadTemplatePreview(selectedTemplate.id, null, categorySlug);
    }
  };

  const handleSendNewsletter = async () => {
    if (!selectedTemplate) return;
    
    if (!window.confirm(`Send this newsletter to ${stats?.active || 0} subscribers?`)) {
      return;
    }

    setSending(true);
    try {
      let res;
      if (selectedTemplate.id === 'custom') {
        if (!customSubject || !customContent) {
          toast.error('Please fill in subject and content');
          setSending(false);
          return;
        }
        res = await newsletterAPI.broadcast({ subject: customSubject, content: customContent });
      } else {
        res = await newsletterAPI.sendTemplate(
          selectedTemplate.id,
          selectedArticleId || undefined,
          selectedCategorySlug || undefined
        );
      }
      
      toast.success(`Newsletter sent to ${res.data.sent} subscribers!`);
      setShowBroadcast(false);
      resetDialog();
    } catch (error) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send newsletter');
    } finally {
      setSending(false);
    }
  };

  const resetDialog = () => {
    setSelectedTemplate(null);
    setPreviewHtml('');
    setPreviewSubject('');
    setSelectedArticleId('');
    setSelectedCategorySlug('');
    setCustomSubject('');
    setCustomContent('');
  };

  if (loading && !subscribers.length) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-newsletter">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold">Newsletter</h1>
            <p className="text-muted-foreground">Manage your newsletter subscribers</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={exporting || !stats?.active}
              data-testid="export-subscribers-btn"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export CSV
            </Button>
            <Button
              onClick={() => setShowBroadcast(true)}
              disabled={!stats?.active}
              data-testid="send-newsletter-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Newsletter
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Subscribers</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total?.toLocaleString() || 0}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
              <Mail className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats?.active?.toLocaleString() || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Inactive</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-muted-foreground">{stats?.inactive?.toLocaleString() || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Last 30 Days</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.last_30_days?.toLocaleString() || 0}</div>
              <p className="text-xs text-muted-foreground">new subscribers</p>
            </CardContent>
          </Card>
        </div>

        {/* Source Breakdown */}
        {stats?.by_source && Object.keys(stats.by_source).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Subscriptions by Source</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {Object.entries(stats.by_source).map(([source, count]) => (
                  <div key={source} className="bg-secondary/50 px-4 py-2 rounded-sm">
                    <p className="text-sm text-muted-foreground capitalize">{source}</p>
                    <p className="text-xl font-bold">{count}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subscribers List */}
        <Card>
          <CardHeader>
            <CardTitle>Subscribers ({total})</CardTitle>
          </CardHeader>
          <CardContent>
            {subscribers.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2 font-medium text-muted-foreground">Email</th>
                        <th className="text-left py-3 px-2 font-medium text-muted-foreground">Name</th>
                        <th className="text-left py-3 px-2 font-medium text-muted-foreground">Source</th>
                        <th className="text-left py-3 px-2 font-medium text-muted-foreground">Subscribed</th>
                        <th className="text-left py-3 px-2 font-medium text-muted-foreground">Status</th>
                        <th className="text-right py-3 px-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((sub) => (
                        <tr key={sub.id} className="border-b border-border last:border-0">
                          <td className="py-3 px-2">{sub.email}</td>
                          <td className="py-3 px-2">{sub.name || '-'}</td>
                          <td className="py-3 px-2 capitalize">{sub.source}</td>
                          <td className="py-3 px-2 text-sm text-muted-foreground">
                            {formatDateShort(sub.subscribed_at)}
                          </td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 text-xs rounded-sm ${sub.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                              {sub.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(sub.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {total > limit && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <p className="text-sm text-muted-foreground">
                      Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p - 1)}
                        disabled={page === 0}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={(page + 1) * limit >= total}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-center text-muted-foreground py-8">No subscribers yet</p>
            )}
          </CardContent>
        </Card>

        {/* Newsletter Dialog */}
        <Dialog open={showBroadcast} onOpenChange={(open) => { setShowBroadcast(open); if (!open) resetDialog(); }}>
          <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Send Newsletter</DialogTitle>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto">
              {!selectedTemplate ? (
                // Template Selection
                <div className="py-4">
                  <p className="text-sm text-muted-foreground mb-4">Choose a template to get started:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {TEMPLATE_TYPES.map((template) => {
                      const Icon = template.icon;
                      return (
                        <button
                          key={template.id}
                          onClick={() => handleSelectTemplate(template)}
                          className="p-4 border border-border rounded-sm hover:border-primary/50 hover:bg-secondary/30 transition-colors text-left"
                          data-testid={`template-${template.id}`}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`p-2 rounded-sm ${template.color}`}>
                              <Icon className="h-4 w-4 text-white" />
                            </div>
                            <span className="font-medium">{template.name}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{template.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                // Template Configuration & Preview
                <div className="py-4 space-y-4">
                  {/* Back button and template info */}
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => setSelectedTemplate(null)}>
                      ← Back
                    </Button>
                    <div className={`p-2 rounded-sm ${selectedTemplate.color}`}>
                      {React.createElement(selectedTemplate.icon, { className: "h-4 w-4 text-white" })}
                    </div>
                    <span className="font-medium">{selectedTemplate.name}</span>
                  </div>

                  {/* Subscriber count */}
                  <div className="bg-secondary/50 p-3 rounded-sm text-sm">
                    <p className="font-medium">Sending to: {stats?.active || 0} active subscribers</p>
                  </div>

                  {/* Article selection for breaking_news and new_article */}
                  {selectedTemplate.needsArticle && (
                    <div className="space-y-2">
                      <Label>Select Article</Label>
                      <Select value={selectedArticleId} onValueChange={handleArticleSelect}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose an article..." />
                        </SelectTrigger>
                        <SelectContent>
                          {articles.map((article) => (
                            <SelectItem key={article.id} value={article.id}>
                              {article.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Category selection for category_spotlight */}
                  {selectedTemplate.needsCategory && (
                    <div className="space-y-2">
                      <Label>Select Category</Label>
                      <Select value={selectedCategorySlug} onValueChange={handleCategorySelect}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a category..." />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.slug} value={cat.slug}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Custom template fields */}
                  {selectedTemplate.id === 'custom' && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Subject Line *</Label>
                        <Input
                          placeholder="Your newsletter subject..."
                          value={customSubject}
                          onChange={(e) => setCustomSubject(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Content *</Label>
                        <Textarea
                          placeholder="Write your newsletter content... (HTML supported)"
                          rows={8}
                          value={customContent}
                          onChange={(e) => setCustomContent(e.target.value)}
                          className="font-mono text-sm"
                        />
                        <p className="text-xs text-muted-foreground">
                          HTML tags supported: &lt;p&gt;, &lt;strong&gt;, &lt;a href=""&gt;, &lt;br&gt;, &lt;h2&gt;, &lt;h3&gt;
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Preview */}
                  {selectedTemplate.id !== 'custom' && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <Eye className="h-4 w-4" />
                          Email Preview
                        </Label>
                        {previewSubject && (
                          <span className="text-xs text-muted-foreground">Subject: {previewSubject}</span>
                        )}
                      </div>
                      
                      {loadingPreview ? (
                        <div className="flex items-center justify-center h-[300px] bg-secondary/30 rounded-sm">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : previewHtml ? (
                        <div 
                          className="border border-border rounded-sm overflow-hidden bg-white"
                          style={{ maxHeight: '400px', overflowY: 'auto' }}
                        >
                          <iframe
                            srcDoc={previewHtml}
                            className="w-full h-[400px] border-0"
                            title="Email Preview"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-[200px] bg-secondary/30 rounded-sm text-muted-foreground">
                          {selectedTemplate.needsArticle ? 'Select an article to preview' : 
                           selectedTemplate.needsCategory ? 'Select a category to preview' : 
                           'No preview available'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {selectedTemplate && (
              <DialogFooter className="border-t pt-4">
                <Button variant="outline" onClick={() => { setShowBroadcast(false); resetDialog(); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSendNewsletter} 
                  disabled={
                    sending || 
                    (selectedTemplate.needsArticle && !selectedArticleId) ||
                    (selectedTemplate.needsCategory && !selectedCategorySlug) ||
                    (selectedTemplate.id === 'custom' && (!customSubject || !customContent)) ||
                    (selectedTemplate.id !== 'custom' && !previewHtml)
                  }
                  data-testid="confirm-send-btn"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send to {stats?.active || 0} subscribers
                    </>
                  )}
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminNewsletter;
